import type { ReactNode } from "react";

import { CompanyFacts } from "../components/CompanyFacts";
import { Icon, IconName } from "../components/Icon";
import { ReportMockup } from "../components/ReportMockup";
import {
  COMPANY,
  DASH,
  EJOR_EXPIRY_HOURS,
  AIRTIME_MAX_NAIRA,
  BANK_TRANSFER,
  CABLE_SERVICE_FEE_NAIRA,
  ELECTRICITY_FEE,
  REPORT,
  WHATSAPP,
} from "../config/company";
import {
  DashSection,
  EjorSection,
  HeroChat,
  naira,
  SchedulesSection,
  ShortcutsSection,
} from "./HomeShowcases";

const PROVIDERS = [
  "MTN",
  "Airtel",
  "Glo",
  "9mobile",
  "DStv",
  "GOtv",
  "StarTimes",
  "Electricity",
];

const BILLS: {
  icon: IconName;
  title: string;
  body: string;
  example: string;
}[] = [
  {
    icon: "phone",
    title: "Airtime",
    body: `Top up any MTN, Airtel, Glo or 9mobile line, yours or someone else's, up to ${naira(AIRTIME_MAX_NAIRA)} at a time. No fee.`,
    example: "“₦1,000 airtime to my number”",
  },
  {
    icon: "signal",
    title: "Mobile data",
    body: "Tell it your budget and Hethera lists the daily, weekly and monthly plans that fit. No fee.",
    example: "“What Airtel data can I get for ₦500?”",
  },
  {
    icon: "tv",
    title: "Cable TV",
    body: `Renew or change your DStv, GOtv or StarTimes package with your smartcard number. A ${naira(CABLE_SERVICE_FEE_NAIRA)} service fee is added, shown before you confirm.`,
    example: "“Renew my GOtv Max”",
  },
  {
    icon: "bolt",
    title: "Electricity",
    body: `Buy prepaid tokens or pay postpaid bills for your meter. Your token arrives in the chat. What you enter is what you pay: the card processing fee comes out of it, and you see the units before you confirm.`,
    example: "“₦5,000 light for my meter at home”",
  },
];

const SECURITY: { icon: IconName; title: string; body: string }[] = [
  {
    icon: "lock",
    title: "Your PIN never goes into the chat",
    body: "You enter your PIN in an encrypted WhatsApp form, not as a message. It is stored hashed. The AI assistant never sees it, and repeated wrong attempts lock the account.",
  },
  {
    icon: "card",
    title: "We never see your full card number",
    body: "You enter your card details on Paystack's checkout page. Hethera keeps only a payment token, the card's first six and last four digits, its type, your bank's name, and a fingerprint Paystack provides so we can recognise the same card without seeing its number.",
  },
  {
    icon: "refund",
    title: "Automatic refunds when a purchase fails",
    body: "If your card is charged but the provider does not deliver, Hethera refunds your card automatically. If the bank or provider doesn't give a clear answer, we keep checking every few minutes until we know, then deliver or refund. You do not need to file a complaint first.",
  },
  {
    icon: "ledger",
    title: "Never charged twice",
    body: "Each payment is recorded in a double-entry ledger, with an audit record for every status change. A request sent twice is only charged once, and a declined card is never charged again unless you ask for a new purchase yourself.",
  },
  {
    icon: "eye",
    title: "Watched for fraud",
    body: "Automated checks look for unusual activity, such as many purchases in a few minutes, repeated card declines, or the same card on several accounts. Anything unusual is flagged for our team to review.",
  },
  {
    icon: "shield",
    title: "You confirm every payment",
    body: "Nothing is bought until you enter your PIN. Hethera shows the full amount first. When someone pays a bill for you with Ejor, they confirm with their own PIN.",
  },
  {
    icon: "chat",
    title: "No marketing messages",
    body: "Hethera replies to conversations you start and does not send marketing broadcasts. Messages you get without writing first are about your own scheduled payments (a reminder and the result), a Dash gift someone sent you, or an Ejor request from someone you know if you have turned Ejor on.",
  },
];

const FEATURES: { title: string; body: string; soon?: boolean }[] = [
  {
    title: "Saved beneficiaries",
    body: "Save Mum's line, the office meter or your GOtv smartcard, then pay them by name.",
  },
  {
    title: "Plans that fit your budget",
    body: "Tell Hethera how much you want to spend and it lists only the plans that fit, with prices.",
  },
  {
    title: "Spending reports",
    body: "Ask “how much did I spend on data last month?” and get the answer in the chat, or send REPORT for a private page of charts showing what you bought, for whom and when.",
  },
  {
    title: "Save after you buy",
    body: "After you pay a new number, meter or smartcard, Hethera offers to save it, so next time you just say the name, or put it on a schedule.",
  },
];

export const FAQS: { q: string; a: ReactNode }[] = [
  {
    q: "Is Hethera a bank?",
    a: "No. Hethera is a bill-payment service. We do not hold deposits or keep a wallet balance for you. Each purchase is charged directly to the card you saved, processed by Paystack. Airtime, data, cable and electricity are fulfilled through our bill-payment partner, VTpass.",
  },
  {
    q: "How do I pay?",
    a: "With a debit or credit card only. You add your card once through Paystack's secure page, and each purchase is charged to it after you confirm with your PIN. Hethera doesn't take bank transfers, USSD, cash or wallet top-ups, and we never ask you to send money to an account number. If anyone asks you to pay Hethera by transfer, it isn't us.",
  },
  {
    q: "How much does it cost?",
    a: `Airtime and data cost exactly the amount you buy, with no fee. Airtime is up to ${naira(AIRTIME_MAX_NAIRA)} per top-up. Cable TV has a ${naira(CABLE_SERVICE_FEE_NAIRA)} service fee on top of the plan. For electricity, the amount you enter is what your card pays: the card processing fee (${ELECTRICITY_FEE.pct}% + ${naira(ELECTRICITY_FEE.flat)}, or just ${ELECTRICITY_FEE.pct}% under ${naira(ELECTRICITY_FEE.flatFrom)}) comes out of it and the rest is your units, so ₦5,000 gets you ₦4,825 of electricity.${
      BANK_TRANSFER.live
        ? ` For ${naira(BANK_TRANSFER.minimum)} or more you can pay by bank transfer instead, where the fee is ${BANK_TRANSFER.pct}% and never more than ${naira(BANK_TRANSFER.cap)}, so ₦100,000 gets you ₦99,700 of electricity.`
        : ""
    } Hethera shows every figure before you confirm with your PIN, and nothing is charged until you confirm. If a purchase fails, the whole amount is refunded, fee included.`,
  },
  {
    q: "What happens if my payment fails?",
    a: "If your card was charged but the provider did not deliver, we refund your card automatically and tell you in the chat. How long the money takes to appear depends on your bank. If anything is unclear, contact support with the reference in your receipt.",
  },
  {
    q: "I forgot my PIN, or my phone was stolen.",
    a: (
      <>
        Email{" "}
        <a href={`mailto:${COMPANY.supportEmail}`}>{COMPANY.supportEmail}</a>{" "}
        from any device. We will lock your account and help you reset your PIN.
        Repeated wrong PIN attempts also lock the account automatically.
      </>
    ),
  },
  {
    q: "Can I see how much I have spent?",
    a: `Yes. Ask Hethera, for example “how much did I spend on airtime in September?”, or send REPORT to choose a period. You get a private link to a page of charts at ${REPORT.host}. The link works once, expires after ${REPORT.linkHours} hours if you don't open it, and we tell you in the chat when it has been opened. If that wasn't you, reply REPORT STOP.`,
  },
  {
    q: "Can someone else pay a bill for me?",
    a: `Yes, with Ejor. Send EJOR, pick the bill and enter the WhatsApp number of the person you'd like to pay. They must already use Hethera, have made a purchase and have turned Ejor on. They see your name, your number and the bill, and pay with their own card and PIN. Unpaid requests expire after ${EJOR_EXPIRY_HOURS} hours. To stop receiving requests, send EJOR OFF.`,
  },
  {
    q: "Can several people share one Ejor?",
    a: "No. Each Ejor goes to one person, who pays the whole bill. If they decline or the request expires, you can start a new Ejor with someone else.",
  },
  {
    q: "How does a Dash gift work?",
    a: `Send DASH and choose who it's for, airtime or electricity, the amount and when it should arrive. You pay straight away. On that day they get a message and choose the phone number and network, or the meter, and it's bought there and then. If they tap No thanks or don't accept within ${DASH.responseDays} days, your card is refunded in full. They must already use Hethera.`,
  },
  {
    q: "Can a scheduled payment charge me more than I expect?",
    a: "No. When you set it up you choose the most each payment may cost, and confirm with your PIN. If a plan costs more than that on the day, the payment is skipped and we tell you. A payment that fails is never tried again on its own.",
  },
  {
    q: "How do I delete my account?",
    a: (
      <>
        Follow the steps on our <a href="/data-deletion">data deletion page</a>.
        We remove your saved card token and profile, and keep transaction
        records only for as long as the law requires.
      </>
    ),
  },
];

export function HomePage() {
  return (
    <>
      <section className="container hero" aria-labelledby="hero-title">
        <div className="hero__copy">
          <p className="hero__badge">
            <span className="hero__badge-dot" aria-hidden="true" />
            Bill payments for Nigeria, right inside WhatsApp
          </p>
          <h1 id="hero-title">
            <span className="h1-line">
              <span>Pay any bill.</span>
            </span>{' '}
            <span className="h1-line">
              <span>Just text it.</span>
            </span>
          </h1>
          <p className="hero__sub">
            Hethera is a bill-payment assistant on WhatsApp. Buy airtime and
            data, renew your cable TV and top up your meter by sending a
            message. Ask someone to pay a bill for you, send a gift, or put a
            bill on a schedule. Every payment is confirmed with your PIN before
            any money moves.
          </p>
          <div className="hero__ctas">
            <a className="btn btn-accent btn-lg" href={WHATSAPP.href}>
              <Icon name="chat" />
              Start on WhatsApp
            </a>
            <a className="btn btn-ghost btn-lg" href="#how">
              See how it works
            </a>
          </div>
          <ul className="hero__checks">
            {[
              "No app to download",
              "Card payments secured by Paystack",
              "Automatic refund if a purchase fails",
            ].map((t) => (
              <li key={t}>
                <Icon
                  name="check"
                  size={16}
                  color="var(--ok)"
                  strokeWidth={2.4}
                />
                {t}
              </li>
            ))}
          </ul>
          <QrCard className="qr-card--desktop" />
        </div>
        <div className="hero__visual">
          <div className="hero__sticky">
            <HeroChat />
          </div>
        </div>
      </section>

      <section className="providers" aria-label="Supported providers">
        <div className="container providers__inner">
          <span className="eyebrow">Works with</span>
          {PROVIDERS.map((p) => (
            <span key={p} className="providers__name">
              {p}
            </span>
          ))}
        </div>
      </section>

      <section
        id="pay"
        className="container section"
        aria-labelledby="pay-title"
      >
        <div className="section-head">
          <div>
            <h2 id="pay-title" className="display">
              Four everyday bills. One chat.
            </h2>
          </div>
          <p className="lede">
            Write the way you would to a friend. Hethera finds the right plan,
            checks the details and asks you to confirm.
          </p>
        </div>
        <ul className="card-row">
          {BILLS.map((b) => (
            <li key={b.title} className="bill-card">
              <span className="bill-card__icon">
                <Icon name={b.icon} size={22} color="var(--accent)" />
              </span>
              <h3>{b.title}</h3>
              <p>{b.body}</p>
              <span className="bill-card__example">{b.example}</span>
            </li>
          ))}
        </ul>
      </section>

      <section
        id="how"
        className="container section"
        style={{ paddingTop: 0 }}
        aria-labelledby="how-title"
      >
        <div className="section-head">
          <div>
            <p className="eyebrow">How it works</p>
            <h2 id="how-title" className="display">
              Set up once. Pay in seconds after that.
            </h2>
          </div>
        </div>
        <ol className="card-row">
          <li className="step">
            <span className="step__num">1</span>
            <h3>Say hello</h3>
            <p>
              Message Hethera on WhatsApp. Your account is created from your
              WhatsApp number. No forms, no passwords and no app to download.
            </p>
          </li>
          <li className="step">
            <span className="step__num">2</span>
            <h3>Set up in one form, then add a card</h3>
            <p>
              In a secure WhatsApp form, add your name and email, choose whether
              people can ask you to pay their bills, and create your PIN. Then
              add your debit card on Paystack's checkout page. The small
              verification charge is refunded.
            </p>
          </li>
          <li className="step step--accent">
            <span className="step__num">3</span>
            <h3>Ask, confirm, done</h3>
            <p>
              Ask for what you need and confirm with your PIN. Your card is
              charged and you get a receipt in the chat.
            </p>
          </li>
        </ol>
      </section>

      <EjorSection />
      <DashSection />
      <SchedulesSection />

      <section id="security" className="dark" aria-labelledby="security-title">
        <div className="container section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Security</p>
              <h2 id="security-title" className="display">
                Easy to use, with strict security behind it.
              </h2>
            </div>
            <p className="lede">
              The chat is simple to use. Every payment still goes through the
              same checks, in the same order.
            </p>
          </div>
          <ul className="sec-grid">
            {SECURITY.map((s) => (
              <li key={s.title} className="sec-item">
                <Icon
                  name={s.icon}
                  size={26}
                  color="var(--accent)"
                  strokeWidth={1.8}
                />
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="reports"
        className="container section"
        aria-labelledby="reports-title"
      >
        <div className="split">
          <div className="split__aside">
            <p className="eyebrow">Spending reports</p>
            <h2
              id="reports-title"
              className="display"
              style={{ fontSize: "clamp(36px, 4vw, 52px)" }}
            >
              See where your money went.
            </h2>
            <p className="lede">
              Send REPORT, or just ask. Hethera sends a private link to charts
              of what you spent: by bill, by provider, who you bought for and
              when you usually buy.
            </p>
            <ul className="hero__checks">
              {[
                "The link works once",
                `It expires after ${REPORT.linkHours} hours if unopened`,
                "Phone and meter numbers are partly hidden",
                "We tell you in the chat when it's opened",
              ].map((t) => (
                <li key={t}>
                  <Icon
                    name="check"
                    size={16}
                    color="var(--ok)"
                    strokeWidth={2.4}
                  />
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="split__main split__main--report">
            <ReportMockup />
          </div>
        </div>
      </section>

      <section className="container section" aria-labelledby="features-title">
        <div className="split">
          <div className="split__aside">
            <p className="eyebrow">Also in Hethera</p>
            <h2
              id="features-title"
              className="display"
              style={{ fontSize: "clamp(36px, 4vw, 52px)" }}
            >
              Small features that save you time every month.
            </h2>
            <a
              className="btn btn-dark"
              href={WHATSAPP.href}
              style={{ alignSelf: "flex-start", marginTop: 8 }}
            >
              Try it on WhatsApp
            </a>
          </div>
          <ol className="feature-list split__main">
            {FEATURES.map((f, i) => (
              <li key={f.title}>
                <span className="feature-list__num">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3>
                    {f.title}
                    {f.soon && <span className="tag">Coming soon</span>}
                  </h3>
                  <p>{f.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <ShortcutsSection />

      <section id="faq" className="faq-wrap" aria-labelledby="faq-title">
        <div className="container section split">
          <div className="split__aside">
            <h2
              id="faq-title"
              className="display"
              style={{ fontSize: "clamp(36px, 4vw, 52px)" }}
            >
              Questions, answered.
            </h2>
            <p className="lede">
              Still unsure? Email{" "}
              <a href={`mailto:${COMPANY.supportEmail}`}>
                {COMPANY.supportEmail}
              </a>{" "}
              or ask Hethera in the chat.
            </p>
          </div>
          <div className="faq split__main">
            {FAQS.map((f) => (
              <details key={f.q}>
                <summary>
                  {f.q}
                  <span className="faq__plus" aria-hidden="true">
                    +
                  </span>
                </summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section
        id="company"
        className="container section"
        aria-labelledby="company-title"
      >
        <div className="section-head">
          <div>
            <p className="eyebrow">Company</p>
            <h2
              id="company-title"
              className="display"
              style={{ fontSize: "clamp(36px, 4vw, 52px)" }}
            >
              Who runs Hethera
            </h2>
          </div>
          <p className="lede">
            Hethera is built and operated by {COMPANY.legalName}, a company
            registered in Nigeria with the Corporate Affairs Commission.
          </p>
        </div>
        <div className="company">
          <CompanyFacts />
          <div className="company__side">
            <div className="panel">
              <h3>What we do</h3>
              <p>
                Hethera lets people in Nigeria buy airtime, mobile data, cable
                TV subscriptions and electricity through WhatsApp. Payments are
                made with their own debit card, by someone they know through
                Ejor, or on a schedule they set. Customers can send airtime or
                electricity as a Dash gift and get a report of what they spent.
              </p>
            </div>
            <nav className="panel" aria-labelledby="policies-title">
              <h3 id="policies-title">Policies</h3>
              <a href="/privacy">Privacy Policy</a>
              <a href="/terms">Terms of Service</a>
              <a href="/refund-policy">Refund Policy</a>
              <a href="/data-deletion">Data deletion</a>
            </nav>
          </div>
        </div>
      </section>

      <section className="container" style={{ paddingBottom: 112 }}>
        <div className="cta">
          <h2>Your next bill is one message away.</h2>
          <div className="cta__side">
            <a className="btn btn-dark btn-lg" href={WHATSAPP.href}>
              Message Hethera
              <Icon name="arrow" size={18} />
            </a>
            <QrCard className="qr-card--desktop" />
          </div>
        </div>
      </section>
    </>
  );
}

// On a computer, scan with your phone's camera to open the chat there.
export function QrCard({ className = "" }: { className?: string }) {
  return (
    <a className={`qr-card ${className}`} href={WHATSAPP.href}>
      <img
        src={WHATSAPP.qr}
        alt="QR code that opens Hethera on WhatsApp"
        width={112}
        height={112}
      />
      <p>
        <strong>On a computer?</strong>
        Scan with your phone's camera to open Hethera in WhatsApp.
      </p>
    </a>
  );
}
